import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';

const ArticlePage = () => {
  const { slug } = useParams();

  // Article content data
  const articles = {
    'virtual-autism-epidemic': {
      title: 'The "Virtual Autism" Epidemic: Are We Diagnosing a Neurotype or Environmental Trauma?',
      date: 'December 2025',
      readTime: '8 min read',
      content: `
        <p>Walk into any pediatric OPD in urban India today, and you will see a chillingly identical waiting room. Toddlers—18 months to 3 years old—staring at glowing screens. No eye contact. No response to their name. Parents scrolling through diagnostic checklists on their phones, terrified they are looking at lifelong autism.</p>

        <p>But are we looking at authentic, lifelong neurodivergence, or are we looking at a temporary, environmentally induced neuro-deprivation?</p>

        <h2>The Rise of "Virtual Autism"</h2>
        <p>The term "Virtual Autism" has emerged to describe a phenomenon where excessive screen exposure in early childhood mimics the behavioral presentation of Autism Spectrum Disorder (ASD). Children display reduced eye contact, delayed language acquisition, repetitive behaviors, and apparent social withdrawal—all hallmark features of autism.</p>

        <p>However, unlike neurodevelopmental autism, these symptoms can be <strong>reversible</strong> when screen time is eliminated and replaced with rich, multi-sensory environmental engagement.</p>

        <h2>The Diagnostic Dilemma</h2>
        <p>The challenge for clinicians is profound: How do we differentiate between authentic autism and screen-induced developmental delay in a 2-year-old? Both presentations look identical on surface-level observation. Both will score similarly on parent-reported checklists. Both may even show similar patterns on certain observational assessments.</p>

        <p>The difference emerges only through:</p>
        <ul>
          <li><strong>Detailed developmental history</strong>: Was there a clear regression following intensive screen exposure?</li>
          <li><strong>Environmental audit</strong>: What is the child's daily sensory diet? How much human interaction versus passive screen time?</li>
          <li><strong>Trial intervention</strong>: Do symptoms improve dramatically with screen elimination and environmental enrichment?</li>
        </ul>

        <h2>The Neurobiological Mechanism</h2>
        <p>Excessive screen exposure during critical developmental windows disrupts the formation of neural pathways essential for social cognition, language processing, and executive function. The two-dimensional, rapid-fire, hyper-stimulating nature of digital content cannot substitute for the complex, multi-sensory, contingent interactions required for healthy brain development.</p>

        <p>What we are witnessing is not a genetic neurotype but an <strong>environmental deprivation syndrome</strong>—a form of modern neglect that masquerades as a neurodevelopmental disorder.</p>

        <h2>Clinical Implications</h2>
        <p>For families navigating this diagnostic uncertainty, the approach must be rigorous:</p>
        <ol>
          <li><strong>Comprehensive Assessment</strong>: Utilize gold-standard tools (ADOS-2, ADI-R) alongside detailed environmental audits.</li>
          <li><strong>Trial Intervention Period</strong>: Implement a 6-8 week complete screen elimination protocol with intensive environmental enrichment.</li>
          <li><strong>Reassessment</strong>: Observe for developmental catch-up. Virtual autism typically shows rapid improvement; authentic autism requires sustained, specialized intervention.</li>
        </ol>

        <h2>The Broader Question</h2>
        <p>The rise of virtual autism forces us to confront an uncomfortable reality: We are conducting an uncontrolled experiment on the developing brains of an entire generation. The question is no longer whether screens impact neurodevelopment—the evidence is overwhelming. The question is: How do we build clinical frameworks that distinguish between neurotype and environmental trauma in an increasingly digital world?</p>
      `
    },
    'masking-bill-model-students': {
      title: 'The Masking Bill: Why "Model Students" Collapse at Home',
      date: 'December 2025',
      readTime: '10 min read',
      content: `
        <p>The phenomenon known as "After-School Restraint Collapse" is frequently misdiagnosed as behavioral defiance, mood dysregulation, or even emerging conduct disorder. However, a rigorous neuropsychological audit suggests a far more complex metabolic reality: the <strong>physiological debt</strong> incurred by neurodivergent children masking in neuro-normative environments.</p>

        <h2>The Perfect Student Paradox</h2>
        <p>Teachers report a model student: compliant, focused, socially appropriate. Parents, however, describe a completely different child at home: explosive meltdowns, emotional dysregulation, refusal to engage in basic tasks, and what appears to be selective defiance.</p>

        <p>The disconnect is not manipulative behavior. It is <strong>neurological exhaustion</strong>.</p>

        <h2>The Masking Mechanism</h2>
        <p>Neurodivergent children—particularly those with autism, ADHD, or sensory processing differences—often develop sophisticated compensatory strategies to appear "typical" in structured environments. This process, known as masking or camouflaging, involves:</p>

        <ul>
          <li>Suppressing natural stims and self-regulatory behaviors</li>
          <li>Mimicking neurotypical social scripts despite internal confusion</li>
          <li>Manually processing sensory input that neurotypical peers filter automatically</li>
          <li>Consciously managing executive functions (attention, impulse control, task initiation) that others execute unconsciously</li>
        </ul>

        <p>Each of these requires <strong>active cognitive effort</strong>. It is the neurological equivalent of running a marathon while pretending to walk.</p>

        <h2>The Autonomic Nervous System Cost</h2>
        <p>Masking places the child in a state of chronic sympathetic nervous system activation (fight-or-flight). Heart rate variability decreases. Cortisol levels remain elevated. The body operates in survival mode for 6-8 hours straight.</p>

        <p>When the child returns home—the only "safe" environment—the system crashes. The parasympathetic nervous system attempts to restore equilibrium, but the accumulated stress load results in what appears as behavioral collapse.</p>

        <p>This is not defiance. It is <strong>dysregulation</strong>.</p>

        <h2>The Diagnostic Blind Spot</h2>
        <p>Standard diagnostic protocols rely heavily on teacher reports and structured observations. A child who masks effectively will score in the "typical" or "subclinical" range on many assessments, leading to underdiagnosis or delayed intervention.</p>

        <p>Meanwhile, parents are left bewildered, often blamed for "inconsistent parenting" or "enabling" behaviors that are, in reality, adaptive responses to a child in neurological distress.</p>

        <h2>Clinical Intervention Strategy</h2>
        <p>Addressing the masking bill requires a multi-tiered approach:</p>

        <h3>1. Environmental Modifications</h3>
        <p>Work with schools to reduce masking pressure through:</p>
        <ul>
          <li>Sensory breaks and movement opportunities</li>
          <li>Permission to stim or self-regulate visibly</li>
          <li>Modified social demands (e.g., opt-out options for group work)</li>
        </ul>

        <h3>2. Home Recovery Protocols</h3>
        <p>Parents must understand that post-school collapse is <strong>medical</strong>, not behavioral. The home becomes a decompression zone:</p>
        <ul>
          <li>Minimal demands for the first hour post-school</li>
          <li>Access to preferred sensory regulation activities</li>
          <li>No punishment for dysregulated behavior (co-regulation, not consequences)</li>
        </ul>

        <h3>3. Long-Term Nervous System Support</h3>
        <p>Interventions targeting autonomic regulation:</p>
        <ul>
          <li>Interoception training (body awareness)</li>
          <li>Polyvagal-informed therapy</li>
          <li>Occupational therapy for sensory integration</li>
        </ul>

        <h2>The Broader Implication</h2>
        <p>The masking bill is not a diagnosis; it is a <strong>systemic failure</strong>. We have created educational and social environments that reward conformity and punish natural neurodivergent expression. Until we build systems that accommodate, rather than suppress, neurological differences, we will continue to see children paying an invisible, metabolic price for their survival in neuro-normative spaces.</p>
      `
    },
    'diagnostic-blind-spot': {
      title: 'The Diagnostic Blind Spot: Measuring Internal Distress vs. External Disruption',
      date: 'December 2025',
      readTime: '9 min read',
      content: `
        <p>In neurodevelopmental diagnostics, the child who slips through the cracks isn't the one struggling to sit still, throwing books, or refusing to follow instructions. It is the "perfect" student—the one who sits quietly, completes assignments, and never disrupts class. The one whose internal autonomic storm goes entirely unnoticed because it produces no external chaos.</p>

        <h2>The Externalization Bias</h2>
        <p>Current diagnostic frameworks are heavily weighted toward <strong>observable, disruptive behaviors</strong>. ADHD criteria emphasize hyperactivity and impulsivity. Autism assessments prioritize visible social deficits. Anxiety disorders require observable avoidance or panic.</p>

        <p>But what about the child whose distress manifests as:</p>
        <ul>
          <li>Perfectionism and over-control</li>
          <li>Internal rumination and obsessive thought loops</li>
          <li>Extreme masking and people-pleasing</li>
          <li>Somatic symptoms (stomachaches, headaches, fatigue)</li>
        </ul>

        <p>These children are <strong>clinically invisible</strong>—until they collapse.</p>

        <h2>The Camouflaging Cost</h2>
        <p>Research into autistic camouflaging reveals a disturbing pattern: individuals who mask effectively experience <strong>higher rates of anxiety, depression, and suicidal ideation</strong> than those who do not mask—despite appearing more "functional" on the surface.</p>

        <p>The diagnostic system rewards external conformity, failing to measure the internal cost of that conformity.</p>

        <h3>Case Example: The "Gifted" Student</h3>
        <p>A 10-year-old is referred for assessment after refusing to attend school. Teachers describe an academically advanced, socially appropriate student. Parents describe nightly meltdowns, obsessive rituals, and increasing self-harm.</p>

        <p>Standard assessments show:</p>
        <ul>
          <li>No observable hyperactivity or inattention (ADHD ruled out)</li>
          <li>No overt social deficits in structured settings (Autism ruled out)</li>
          <li>No external behavioral disruption (ODD ruled out)</li>
        </ul>

        <p>The child is diagnosed with "situational anxiety" and discharged with generic CBT recommendations.</p>

        <p>What was missed: A profound autonomic nervous system dysregulation, masked by extreme cognitive compensation. The child was manually processing every social cue, suppressing natural stims, and operating in a state of chronic hypervigilance—all invisible to standard diagnostic tools.</p>

        <h2>Measuring the Invisible</h2>
        <p>To address this diagnostic blind spot, we need assessment protocols that measure <strong>internal distress</strong>, not just external disruption:</p>

        <h3>1. Autonomic Nervous System Profiling</h3>
        <ul>
          <li>Heart rate variability (HRV) monitoring during structured tasks</li>
          <li>Cortisol sampling (diurnal patterns)</li>
          <li>Galvanic skin response (GSR) as a proxy for sympathetic activation</li>
        </ul>

        <h3>2. Self-Report Interoception Scales</h3>
        <ul>
          <li>Assessing internal body awareness and distress signals</li>
          <li>Measuring discrepancy between internal state and external presentation</li>
        </ul>

        <h3>3. Parent/Teacher Discrepancy Analysis</h3>
        <ul>
          <li>Significant differences between home and school behavior are not "inconsistency"—they are diagnostic data</li>
          <li>Children who "hold it together" at school and collapse at home are showing masking behavior, not behavioral manipulation</li>
        </ul>

        <h2>Clinical Implications</h2>
        <p>When a child presents with:</p>
        <ul>
          <li>Extreme perfectionism or rigidity</li>
          <li>Somatic complaints without medical cause</li>
          <li>Significant home/school behavioral discrepancy</li>
          <li>History of "late" diagnosis despite longstanding concerns</li>
        </ul>

        <p>...the clinician must look <strong>beneath the surface</strong>. The absence of external disruption does not indicate the absence of neurodevelopmental challenges. It may indicate the presence of extreme compensatory masking—which carries its own, often more severe, long-term cost.</p>

        <h2>The Systemic Fix</h2>
        <p>We need diagnostic systems that:</p>
        <ol>
          <li>Value internal experience as much as external behavior</li>
          <li>Recognize masking as a clinical phenomenon, not a sign of "mild" presentation</li>
          <li>Incorporate physiological measures alongside behavioral observation</li>
          <li>Train clinicians to identify camouflaging strategies</li>
        </ol>

        <p>Until we do, we will continue to miss the children who need us most—the ones who have learned to hide in plain sight.</p>
      `
    },
    'algorithmic-pathologizing': {
      title: 'Algorithmic Pathologizing: How the Digital Crisis Teaches Youth to Conceal',
      date: 'December 2025',
      readTime: '12 min read',
      content: `
        <p>We frequently discuss how 60-second videos lead to rampant misdiagnosis among adolescents. "TikTok-induced autism," "Instagram anxiety," "algorithmic ADHD"—these terms dominate clinical conversations about digital misinformation.</p>

        <p>But a deeper, darker secondary layer is emerging: <strong>The algorithm is now actively teaching our youth to mask their distress from clinicians.</strong></p>

        <h2>Layer 1: The Self-Diagnosis Epidemic</h2>
        <p>Social media platforms have democratized mental health discourse. On the surface, this appears positive: reducing stigma, increasing awareness, validating experiences.</p>

        <p>In practice, it has created a phenomenon where adolescents consume curated symptom lists, identify with selective traits, and arrive at clinical appointments with pre-formed diagnostic conclusions.</p>

        <p>Clinicians report an alarming trend: teenagers presenting with "autism" or "ADHD" based on viral videos, but unable to provide coherent developmental histories or functional impairment examples that align with diagnostic criteria.</p>

        <h2>Layer 2: The Algorithmic Feedback Loop</h2>
        <p>Here is where it becomes insidious. Once a user engages with mental health content, the algorithm amplifies it. A teenager who watches one video about ADHD will be served hundreds more. The feed becomes a diagnostic echo chamber.</p>

        <p>But the algorithm does not just reinforce self-diagnosis—it <strong>teaches symptom performance</strong>.</p>

        <h3>The "How I Realized I Have ADHD" Genre</h3>
        <p>These videos follow a formula:</p>
        <ol>
          <li>Relatable, everyday behavior (forgetting where you put your keys)</li>
          <li>Reframe as pathology ("This is executive dysfunction")</li>
          <li>Dramatic reveal ("I didn't know I had ADHD until...")</li>
        </ol>

        <p>The teenager internalizes this script. Normal variability in attention, motivation, and organization becomes reframed as disorder. The algorithm has taught them to <strong>pathologize their own experience</strong>.</p>

        <h2>Layer 3: The Concealment Training</h2>
        <p>Now for the most concerning development: The algorithm is teaching youth to <strong>hide authentic distress while performing curated pathology</strong>.</p>

        <h3>The Infographic Below</h3>
        <p>This visual demonstrates the dual process at work:</p>
      `,
      hasInfographic: true,
      infographicUrl: 'https://customer-assets.emergentagent.com/job_distracted-zhukovsky-2/artifacts/f069a8_image.jpg',
      contentAfterInfographic: `
        <p>On one hand, adolescents learn to identify and label benign experiences as symptoms ("I zone out in class = ADHD"). On the other hand, they learn to suppress and conceal genuine emotional distress that does not fit the algorithm-approved narrative.</p>

        <h3>The Clinical Presentation</h3>
        <p>Adolescents arrive at assessments with:</p>
        <ul>
          <li>Highly articulate, rehearsed symptom descriptions (learned from TikTok)</li>
          <li>Insistence on specific diagnoses (often trendy or socially acceptable ones)</li>
          <li>Resistance to diagnostic exploration that challenges their self-narrative</li>
          <li>Simultaneously, <strong>concealment of deeper distress</strong> (trauma, suicidal ideation, substance use) that falls outside the curated identity</li>
        </ul>

        <p>The algorithm has trained them to perform a sanitized, socially acceptable version of pathology while hiding the messy, complex reality underneath.</p>

        <h2>The Clinical Dilemma</h2>
        <p>How do we conduct ethical assessments when the client has been algorithmically trained to:</p>
        <ol>
          <li>Perform symptoms they do not have</li>
          <li>Conceal symptoms they do have</li>
          <li>Resist diagnostic clarity that conflicts with their algorithmic identity</li>
        </ol>

        <h3>Case Example: The "Autistic" Influencer</h3>
        <p>A 16-year-old presents for autism assessment. They have 200K TikTok followers documenting their "autistic experience." They describe sensory sensitivities, social struggles, and special interests—all textbook.</p>

        <p>But when pressed for developmental history, functional impairment, or objective examples, the narrative unravels. The "sensory sensitivity" is selective and performative. The "social struggles" are recent and situational. The "special interests" are algorithmically curated trends.</p>

        <p>Beneath the performed autism identity: severe depression, academic pressure, and profound loneliness—none of which they are willing to explore because it does not fit the brand they have built.</p>

        <h2>The Systemic Response</h2>
        <p>Clinicians must adapt assessment protocols to account for algorithmic interference:</p>

        <h3>1. Developmental Deep Dive</h3>
        <p>Insist on detailed, longitudinal developmental history. Algorithmic identities are recent; authentic neurodevelopmental conditions have lifelong trajectories.</p>

        <h3>2. Functional Impairment Analysis</h3>
        <p>Move beyond symptom checklists. What are the real-world costs? How does the alleged condition impact daily functioning? Performed symptoms rarely come with genuine impairment.</p>

        <h3>3. Collateral Information</h3>
        <p>Parent, teacher, and peer reports often reveal discrepancies between the curated online identity and lived reality.</p>

        <h3>4. Algorithmic Literacy Psychoeducation</h3>
        <p>Directly address the role of social media in shaping the client's self-narrative. This is not dismissive—it is diagnostic clarity.</p>

        <h2>The Broader Crisis</h2>
        <p>We are facing a generation whose sense of self has been algorithmically constructed. Identity, pathology, and performance have merged into an indistinguishable whole.</p>

        <p>The algorithm has done what no clinician would ethically do: It has taught vulnerable adolescents to pathologize normal experience, conceal genuine distress, and resist therapeutic exploration that threatens their curated identity.</p>

        <p>Until we build clinical frameworks that can disentangle authentic neurodevelopmental and mental health challenges from algorithmic identity construction, we will continue to see misdiagnosis, iatrogenic harm, and failure to address the real suffering beneath the performance.</p>

        <p>The algorithm is not just spreading misinformation. It is actively training youth to deceive—and to deceive themselves most of all.</p>
      `
    }
  };

  const article = articles[slug];

  if (!article) {
    return (
      <div className="section">
        <div className="container">
          <h1>Article Not Found</h1>
          <Link to="/insights">Back to Insights</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Article Header */}
      <section className="section" style={{ paddingTop: '6rem', paddingBottom: '3rem' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Link
              to="/insights"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--accent)',
                textDecoration: 'none',
                marginBottom: '2rem',
                fontSize: '0.95rem',
                fontWeight: 500
              }}
            >
              <ArrowLeft size={18} />
              Back to Insights
            </Link>

            <div style={{
              display: 'inline-block',
              background: 'var(--accent)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: 600,
              marginBottom: '1.5rem',
              letterSpacing: '0.05em'
            }}>
              CLINICAL BRIEF
            </div>

            <h1 style={{
              fontSize: 'clamp(2rem, 4vw, 2.5rem)',
              lineHeight: 1.2,
              marginBottom: '1.5rem',
              fontFamily: 'var(--font-heading)'
            }}>
              {article.title}
            </h1>

            <div style={{
              display: 'flex',
              gap: '2rem',
              fontSize: '0.95rem',
              color: 'var(--text-muted)',
              marginBottom: '3rem',
              paddingBottom: '2rem',
              borderBottom: '1px solid var(--border)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                {article.date}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                {article.readTime}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="section" style={{ paddingTop: '0' }}>
        <div className="container">
          <div 
            className="article-content"
            style={{
              maxWidth: '800px',
              margin: '0 auto',
              fontSize: '1.125rem',
              lineHeight: 1.9,
              color: 'var(--text-main)'
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: article.content }} />
            
            {/* Infographic for Algorithmic Pathologizing article */}
            {article.hasInfographic && (
              <div style={{
                margin: '3rem 0',
                padding: '2rem',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                border: '1px solid var(--border)'
              }}>
                <img 
                  src={article.infographicUrl}
                  alt="Algorithmic Pathologizing Infographic"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px'
                  }}
                />
              </div>
            )}
            
            {article.contentAfterInfographic && (
              <div dangerouslySetInnerHTML={{ __html: article.contentAfterInfographic }} />
            )}
          </div>
        </div>
      </section>

      {/* Back to Insights CTA */}
      <section className="section-alt">
        <div className="container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <Link
              to="/insights"
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <ArrowLeft size={18} />
              View All Clinical Briefs
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        .article-content h2 {
          font-size: 1.75rem;
          font-family: var(--font-heading);
          font-weight: 600;
          color: var(--primary);
          margin: 2.5rem 0 1rem;
          line-height: 1.3;
        }

        .article-content h3 {
          font-size: 1.375rem;
          font-family: var(--font-heading);
          font-weight: 600;
          color: var(--primary);
          margin: 2rem 0 1rem;
          line-height: 1.4;
        }

        .article-content p {
          margin-bottom: 1.5rem;
        }

        .article-content ul, .article-content ol {
          margin: 1.5rem 0;
          padding-left: 2rem;
        }

        .article-content li {
          margin-bottom: 0.75rem;
        }

        .article-content strong {
          font-weight: 600;
          color: var(--accent);
        }

        .article-content a {
          color: var(--accent);
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default ArticlePage;
